import { For, Show, createMemo, createSignal, type Component } from 'solid-js';

import { learnSkill, levelSkill } from '../net/protocol';
import type { GameStore } from '../state/store';
import { BULK_SPEND, pointsForClick } from './bulkSpend';
import {
  ACTION_ICONS,
  LMB_SLOT,
  slotKeyLabel,
  slotSigil,
  STRIKE_SIGIL,
} from './actionbar';
import { Icon } from './Icon';
import { useDrag, type DragController } from './DragContext';
import { FloatingTooltip } from './ItemTooltip';
import { SkillTooltipBody } from './SkillTooltip';
import {
  EMPTY_SKILL_QUERY,
  gateText,
  groupSkills,
  isSkillQueryNarrowed,
  lockText,
  rankRefusalText,
  rankText,
  skillRows,
  skillTags,
  tagVocabulary,
  toggleSkillTag,
  type SkillQuery,
  type SkillRow,
} from './skills';

/**
 * The skill panel: the whole catalogue, and what you can do with each entry.
 *
 * Split out of the character sheet rather than living inside it. The sheet
 * already carries a paper doll, four attributes, two columns of derived stats
 * and the bag; a hundred-skill catalogue on top of that is a wall. They are also
 * different questions — "what am I wearing" and "what can I do" — and a panel
 * each means either can be open while the other is not.
 *
 * **Every skill is listed**, including the ones far out of reach. It used to show
 * only what the `CHARACTER` frame carried, which the server trims to what is
 * learned or immediately learnable — so a fresh character opened this and saw an
 * empty list with a sentence of explanation, and never learned that the game has
 * a hundred skills or what any of them do. Locked entries are greyed, sorted last,
 * and say exactly what they are waiting for.
 */
export const SkillsPanel: Component<{ store: GameStore }> = (props) => {
  const sheet = () => props.store.character();
  // The one shared gesture, so a skill dragged out of this panel and an item dragged out
  // of the bag cannot both be in flight.
  const dragging = useDrag();

  const [query, setQuery] = createSignal<SkillQuery>(EMPTY_SKILL_QUERY);
  const patch = (over: Partial<SkillQuery>) => setQuery({ ...query(), ...over });

  const rows = () => {
    const c = sheet();
    return skillRows(
      props.store.skillCatalogue(),
      c?.skills ?? [],
      c?.attributes.total ?? null,
      c?.slotted ?? [],
      c?.progression.skillPoints ?? 0,
    );
  };

  /**
   * The filtered, grouped rows.
   *
   * Memoised: a hundred rows through a filter and five groupings on every render of a
   * panel that also re-renders on every CHARACTER frame is the one place in this panel
   * where the work is worth not repeating.
   */
  const groups = createMemo(() => groupSkills(rows(), query()));
  const shown = () => groups().reduce((n, group) => n + group.rows.length, 0);

  const counts = () => {
    const all = rows();
    return {
      total: all.length,
      learned: all.filter((r) => r.learned).length,
      open: all.filter((r) => r.learnable || (!r.learned && r.gateMet)).length,
    };
  };

  return (
    <Show when={props.store.menu() === 'skills'}>
      <div
        class="sheet hud-interactive"
        data-testid="skills-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Skills"
      >
        {/*
          **Wider and shorter than the character sheet**, which is what `--skills` buys. Reported: the
          panel is tall enough to cover the action bar, and *the keybind bar at the bottom is the thing
          you are assigning to* — so a player had to close the panel to see the result of the click
          they just made. Trading height for width costs nothing here: the rows are a
          `repeat(auto-fill, minmax(280px, …))` grid, so extra width becomes another column rather than
          longer lines.
        */}
        <div class="plate plate--riveted sheet__panel sheet__panel--skills">
          <header class="sheet__head">
            <h2 class="sheet__title">Skills</h2>
            <button
              type="button"
              class="sheet__close"
              data-testid="skills-close"
              onClick={() => props.store.closeMenu()}
              aria-label="Close"
            >
              ✕
            </button>
          </header>

          <Show
            when={sheet()}
            fallback={
              <p class="sheet__waiting" data-testid="skills-waiting">
                Awaiting the server's reckoning of you.
              </p>
            }
          >
            {(c) => (
              <section class="sheet__skills sheet__skills--panel" aria-label="The skill catalogue">
                <p class="sheet__points">
                  <Show
                    when={c().progression.skillPoints > 0}
                    fallback={
                      <span class="sheet__seam">No skill points. Levelling grants one each.</span>
                    }
                  >
                    <strong data-testid="skill-points">{c().progression.skillPoints}</strong> skill
                    point{c().progression.skillPoints === 1 ? '' : 's'} to spend
                  </Show>
                </p>

                <p class="skills__summary" data-testid="skills-summary">
                  {counts().learned} learned · {counts().open} within reach ·{' '}
                  {counts().total} in all
                  <Show when={shown() !== counts().total}>
                    {' '}
                    · <span data-testid="skills-shown">{shown()} shown</span>
                  </Show>
                </p>

                {/*
                  **A hundred skills in one flat list is a list nobody reads.** They were
                  drawn in catalogue order — which is the order they happen to be written
                  in `skills.yaml` — so the question a player actually asks, *what is
                  available to me?*, had no answer on screen at all.
                */}
                <div class="skills__controls hud-interactive">
                  <input
                    type="search"
                    class="vault__search"
                    placeholder="Search name, tag or category…"
                    aria-label="Search the skills"
                    data-testid="skills-search"
                    value={query().text}
                    onInput={(e) => patch({ text: e.currentTarget.value })}
                  />

                  {/*
                    Three states rather than two checkboxes: "usable and not usable" is a
                    query meaning *everything*, and a pair of boxes invites it.
                  */}
                  <label class="vault__sort">
                    Show
                    <select
                      aria-label="Which skills to show"
                      data-testid="skills-availability"
                      value={query().availability}
                      onChange={(e) =>
                        patch({
                          availability: e.currentTarget.value as SkillQuery['availability'],
                        })
                      }
                    >
                      <option value="ALL">Everything</option>
                      <option value="USABLE">Can use</option>
                      <option value="LOCKED">Cannot use</option>
                    </select>
                  </label>

                  <button
                    type="button"
                    class="vault__chip"
                    classList={{ 'vault__chip--on': query().learnedOnly }}
                    data-testid="skills-chip-learned"
                    aria-pressed={query().learnedOnly}
                    onClick={() => patch({ learnedOnly: !query().learnedOnly })}
                  >
                    Learned
                  </button>

                  <Show when={isSkillQueryNarrowed(query())}>
                    <button
                      type="button"
                      class="sigil-button vault__clear"
                      data-testid="skills-clear"
                      onClick={() => setQuery(EMPTY_SKILL_QUERY)}
                    >
                      Clear
                    </button>
                  </Show>
                </div>

                {/* Tags, every one of them read off a field on `SkillInfo` — nothing here
                    invents a taxonomy the YAML would drift away from. */}
                <div class="skills__chips hud-interactive" data-testid="skills-chips">
                  <For each={tagVocabulary(rows())}>
                    {(tag) => (
                      <button
                        type="button"
                        class="vault__chip"
                        classList={{ 'vault__chip--on': query().tags.includes(tag) }}
                        data-testid={`skills-chip-${tag}`}
                        aria-pressed={query().tags.includes(tag)}
                        onClick={() => patch({ tags: toggleSkillTag(query().tags, tag) })}
                      >
                        {tag}
                      </button>
                    )}
                  </For>
                </div>

                {/*
                  ── The basic attack, which is not in the catalogue ──────────────────
                  Asked for directly: *we need a way to assign back the simple attack
                  buttons; the icon should be added in the skill panel.*

                  It has to be **here** and not among the groups below, because it is not a
                  `SkillInfo`: there is no YAML entry, no rank, no cost and no gate, so it
                  cannot be filtered, searched or grouped by an attribute it does not have.
                  Putting it above the filters says the same thing visually — this one is
                  always available and never matches or fails a search.

                  Dragging it sends `slotSkill(slot, '')`, which is the *clear* command. That
                  is not a trick: an empty slot is exactly what the basic attack is, and it
                  means the whole gesture works through machinery that already existed rather
                  than a second path that could disagree with the first.
                */}
                <ul class="skills skills--basic">
                  <BasicAttackEntry store={props.store} dragging={dragging} />
                </ul>

                <Show
                  when={groups().length > 0}
                  fallback={
                    <p class="sheet__seam" data-testid="skills-empty">
                      Nothing matches that.
                    </p>
                  }
                >
                  {/* Grouped by the attribute that gates them, so each skill is in one
                      findable place. Empty groups are dropped: a heading over nothing is
                      noise in a panel whose job is to make a hundred skills findable. */}
                  <For each={groups()}>
                    {(group) => (
                      <section class="skills__group" data-testid={`skills-group-${group.family}`}>
                        <h3 class="skills__group-heading">
                          {group.label}{' '}
                          <span class="skills__group-count">{group.rows.length}</span>
                        </h3>
                        <ul class="skills">
                          <For each={group.rows}>
                            {(row) => (
                              <SkillEntry
                                store={props.store}
                                row={row}
                                points={c().progression.skillPoints}
                                dragging={dragging}
                              />
                            )}
                          </For>
                        </ul>
                      </section>
                    )}
                  </For>
                </Show>
              </section>
            )}
          </Show>
        </div>
      </div>
    </Show>
  );
};

/**
 * The basic attack, as a row a player can pick up.
 *
 * **What it puts on a slot is nothing.** Dragging it sends `slotSkill(slot, '')` — the clear
 * command — because an empty slot *is* the basic attack on LMB. So one gesture reads two ways
 * and both are true: dropped on the left button it restores the swing, and dropped on any
 * other slot it empties that slot, which is the unassign gesture the panel never had.
 *
 * It carries no rank, cost or gate, and says so rather than showing zeroes: this is the one
 * thing on the bar that is always available and costs no point.
 */
const BasicAttackEntry: Component<{ store: GameStore; dragging: DragController }> = (props) => {
  /** Where the basic attack currently is, if the player has left the left button alone. */
  const onLeft = () => (props.store.slotted()[LMB_SLOT] ?? '') === '';

  return (
    <li
      // **Not `skills__row--learned`.** It looked harmless — that class only sets a border
      // colour, which this row wants — and it was a lie the harness read: `learnAndSlot`
      // finds "the first row whose class says learned" to drag, and picked this one, so a
      // browser run reported dragging a skill while actually dragging nothing. The border
      // is `--basic`'s own now, and the row claims only what it is.
      class="skills__row skills__row--basic"
      data-testid="skill-basic-attack"
      // Same gesture as a skill row: a pointer-down that hands the drag to `DragLayer`,
      // which is what makes the ghost follow the cursor without any layout to hit-test.
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        props.dragging.beginSkill('', 'Basic attack', { x: e.clientX, y: e.clientY });
      }}
    >
      {/* Same two-part shape as a skill card — `skills__head` then `skills__desc` — because
          without the head wrapper the name and the note ran together on one line and the note
          overflowed the border. Caught in a browser: `min-width: 0` and the wrap rules live on
          those classes, and jsdom computes no layout to notice their absence. */}
      <div class="skills__head">
        <Icon class="skills__icon" iconId={ACTION_ICONS.strike} glyph={STRIKE_SIGIL} />
        <span class="skills__name">Basic attack</span>
        <span class="skills__gate">{onLeft() ? `On ${slotKeyLabel(LMB_SLOT)}` : 'Unassigned'}</span>
      </div>
      <p class="skills__desc">
        {onLeft()
          ? 'Your weapon swing. Drag it onto a slot to empty that slot.'
          : `Drag it back onto ${slotKeyLabel(LMB_SLOT)} to swing your weapon again.`}
      </p>
    </li>
  );
};

/**
 * One skill.
 *
 * A locked entry is greyed and its buttons are gone, but the row itself is fully
 * hoverable — the whole point of listing it is that the player can read what they
 * are working towards.
 */
const SkillEntry: Component<{
  store: GameStore;
  row: SkillRow;
  points: number;
  /**
   * The shared drag controller.
   *
   * Passed down rather than pulled from context inside the row, so the panel is the one
   * place that requires a `<DragProvider>` — a row that reached for it itself would throw
   * in any spec that rendered a single row outside one.
   */
  dragging: DragController;
}> = (props) => {
  let anchorEl: HTMLLIElement | undefined;
  const [hovered, setHovered] = createSignal(false);
  const def = () => props.row.def;

  /**
   * Why a rank cannot be bought — the **server's** answer where it has given one
   * about this skill, and the row's own derivation otherwise.
   *
   * The server overwrites, for the reason its dodge refusal overwrites the client's:
   * it is the authority, and the moment the two disagree is exactly when a stale
   * frame has told the panel something optimistic.
   */
  const blockedReason = () => {
    const notice = props.store.rankNotice();
    if (notice && notice.skillId === def().skillId) return notice.reason;
    return props.row.levelBlocked;
  };
  const whyNot = () => rankRefusalText(blockedReason(), props.row);

  return (
    <li
      class="skills__row"
      ref={anchorEl}
      classList={{
        'skills__row--learned': props.row.learned,
        'skills__row--disabled': props.row.learned && !props.row.usable,
        'skills__row--locked': props.row.locked,
      }}
      data-testid={`skill-${def().skillId}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      /*
        **A learned skill can be dragged to the action bar.** Slotting used to be a click
        on the row and then a click on a slot number, which is the one place in the HUD
        where the obvious gesture did nothing at all — the bag, the vault and the paper
        doll have all been draggable for a while. Asked for directly.

        Left button only. Right-click has its own meaning everywhere else in the HUD, and
        a gesture that started a drag on any button was the bug that made right-clicking a
        bag cell cancel instead of act.

        **Only what is learned.** Dragging an unlearned skill onto the bar would send a
        command the server refuses, and the refusal would land in the action bar's corner
        as if the player had pressed the key — an answer to a question they did not ask.
      */
      onPointerDown={(event) => {
        if (event.button !== 0 || !props.row.learned) return;
        // The row is not a drag handle for its own buttons: the rank `+` sits inside it,
        // and starting a drag from it would make the button impossible to press.
        if ((event.target as HTMLElement).closest('button')) return;
        event.preventDefault();
        props.dragging.beginSkill(def().skillId, def().name, {
          x: event.clientX,
          y: event.clientY,
        });
      }}
    >
      <div class="skills__head">
        {/* The art, where this build has it. First in the row rather than beside the
            name so the column of icons lines up down the panel — reading a list of a
            hundred skills is a scan, and a scan needs one column to run down. */}
        <Icon class="skills__icon" iconId={def().iconId} glyph={slotSigil(def().name)} />
        <span class="skills__name">{def().name}</span>
        <span class="skills__tier">T{def().tier}</span>
        <Show when={props.row.learned}>
          <span class="skills__rank" data-testid={`rank-${def().skillId}`}>
            {rankText(props.row)}
          </span>
        </Show>
        <span class="skills__gate">{gateText(def())}</span>
      </div>
      <p class="skills__desc">{def().description}</p>

      {/* Every tag is a fact already on `SkillInfo` — what it scales off, what it costs,
          what it commits you to. Ordered rather than alphabetical, so a column of rows
          compares the same position each time. */}
      <div class="skills__tags" data-testid={`tags-${def().skillId}`}>
        <For each={skillTags(def())}>
          {(tag) => <span class="skills__tag">{tag}</span>}
        </For>
      </div>

      <div class="skills__actions">
        <Show when={props.row.locked}>
          <span class="skills__locked" data-testid={`locked-${def().skillId}`}>
            Needs {lockText(props.row)}
          </span>
        </Show>

        {/*
          The rank button. Never `disabled` — a disabled control fires no pointer
          events at all, so it could not be hovered and the row's whole explanation
          would be unreadable on exactly the skills the player is deciding about.
          `aria-disabled` plus a no-op click, the rule the bag already follows.
        */}
        <Show when={props.row.learned}>
          <button
            type="button"
            class="skills__button skills__button--rank"
            classList={{ 'skills__button--off': !props.row.levellable }}
            aria-disabled={!props.row.levellable}
            data-testid={`level-${def().skillId}`}
            title={
              props.row.levellable
                ? `Spend a point on ${def().name} — hold Ctrl for ${BULK_SPEND}`
                : whyNot()
            }
            onClick={(e) => {
              if (!props.row.levellable) return;
              props.store.clearRankNotice();
              // Clamped to the points in hand, not to the ranks remaining: the row does not carry
              // a max, and the server refuses a rank past the ceiling anyway. So a Ctrl-click on a
              // skill two ranks from 20 spends two and is told no three times — correct, and the
              // reason `clearRankNotice` runs first rather than per command.
              const n = pointsForClick(e, props.points);
              for (let i = 0; i < n; i++) props.store.send(levelSkill(def().skillId));
            }}
          >
            + Rank
          </button>
          <Show when={whyNot() !== ''}>
            <span class="skills__why" data-testid={`why-${def().skillId}`}>
              {whyNot()}
            </span>
          </Show>
        </Show>

        <Show when={!props.row.learned && !props.row.locked}>
          <button
            type="button"
            class="skills__button"
            data-testid={`learn-${def().skillId}`}
            disabled={props.points <= 0}
            title={props.points > 0 ? `Learn ${def().name}` : 'No skill points to spend'}
            onClick={() => props.store.send(learnSkill(def().skillId))}
          >
            Learn
          </button>
        </Show>

        <Show when={props.row.learned}>
          {/*
            **The five numbered buttons are gone.** Dragging a row onto the bar already assigned a
            skill — that path exists, is tested, and reads better than a row of digits — so the buttons
            were a second way to do one thing, and the *worse* way: they could only reach slots 1–5, so
            they could not express LMB or RMB at all and quietly implied those two were not assignable.

            What replaced them is what a player can already see: drag the row to the slot. The row shows
            **where the skill currently sits** instead, which is the one thing the buttons said that
            dragging does not.
          */}
          <Show when={props.row.slot >= 0}>
            <span class="skills__slotted" data-testid={`slotted-${def().skillId}`}>
              On {slotKeyLabel(props.row.slot)}
            </span>
          </Show>
          <Show when={!props.row.usable}>
            <span class="skills__warning" data-testid={`disabled-${def().skillId}`}>
              Gate no longer met
            </span>
          </Show>
        </Show>
      </div>

      <FloatingTooltip
        anchor={anchorEl}
        when={hovered()}
        class="skills__tip"
        testId={`skill-tip-${def().skillId}`}
      >
        <SkillTooltipBody row={props.row} store={props.store} />
      </FloatingTooltip>
    </li>
  );
};

